/**
 * SendCommunityVideoMessageUseCase
 *
 * Upload-then-write-then-transcode hop for a single community-wall VIDEO
 * message — the video sibling of `SendCommunityImageMessageUseCase`. This is the
 * community branch of Flutter's `MediasRepository.uploadMedia`
 * (lib/data/repository/medias_repository.dart:258-281), whose two storage halves
 * live in `AppStorageService._uploadCommunityMessageMedia`
 * (lib/services/app_storage_service.dart:216-265). The optimistic entity is built
 * upstream by `CommunityChatMessageRemapper.fromInput` (video branch — Task 16)
 * and inserted into the list by the hook BEFORE this runs — so, like
 * `SendCommunityImageMessageUseCase`, this use case RECEIVES the already-built
 * entity (with its terminal `type = video`) rather than building it. Flutter
 * resolves the `-1` placeholder type to `CommunityChatMessageType.video.index`
 * here (medias_repository.dart:273-276); on web the entity arrives with the
 * terminal type up front (Task 16), so no re-resolution is needed.
 *
 * The pipeline follows the CHAT video flow exactly (web commit b72645d7) with
 * Community-specific resources rather than Flutter's legacy server-side ffmpeg
 * path (sanctioned deviation, story_plan `## Context`): raw upload to the default
 * bucket → Google Transcoder job (encrypted HLS) → community Media-CDN.
 *
 * Ordering (Flutter parity, with one sanctioned reorder):
 *   1. `await` the raw video upload via `CommunityService.uploadCommunityVideo`
 *      and capture `{ uploaded, uploadPath, fileExtension, downloadUrl }` — the
 *      storage-upload half of the community mirror of `_uploadCommunityMessageMedia`
 *      (the owner is `message.messageUsersInfo.owner`, medias_repository.dart:262;
 *      the video CF ffprobes `downloadUrl` REMOTELY, so it is threaded to the
 *      transcode payload as `dynamicUrl`).
 *   2. `await` the message doc write via `CommunityService.addMessage`
 *      UNCONDITIONALLY, regardless of `uploaded` — Flutter's doc write at
 *      medias_repository.dart:278-279 runs after the upload attempt regardless of
 *      the returned bool, so a failed-upload message stays rendered as
 *      "Processing" (matching the image-flow divergence note in `useCommunity`).
 *   3. When `uploaded`, fire the transcode Cloud Function via
 *      `CommunityService.transcodeCommunityVideo` (fire-and-forget; internally
 *      un-awaited). This is gated on upload success exactly as Flutter's
 *      catch-before-fire gates it (app_storage_service.dart:249-265: the `catch`
 *      returns `false` before the callable can fire). `aspectRatio` is read off
 *      the message's OWN `mediaInfo` and forwarded for Flutter payload parity
 *      (`cloud_functions_service.dart:218`); the reworked video CF reads/logs it
 *      but never writes it, so the persisted `mediaInfo.aspectRatio` is the
 *      client's own optimistic doc write (`medias_repository.dart:277`), NOT a CF
 *      echo. `dynamicUrl` is the raw video's
 *      tokenized download URL threaded from step 1; `messageId` is the message's
 *      own `id` (== the Firestore doc id — community_data_source.dart:139
 *      `.doc(message.id)`, and the CF queries `where('id','==',messageId)`).
 *   4. Return the REAL `uploaded` bool (medias_repository.dart:281).
 *
 * Sanctioned ordering deviation (task_12_plan.md:13; image-sibling precedent
 * `sendCommunityImageMessageUseCase.ts:34-42`): the web writes the
 * message doc (step 2) BEFORE firing the callable (step 3). The CF locates the
 * doc via
 * `collectionGroup('community_messages').where('id','==',messageId).orderBy('createdTimestamp','desc').limit(1)`,
 * so the doc must exist before the call. Flutter fires the callable before its
 * doc write (app_storage_service.dart un-awaited fire runs before
 * medias_repository.dart:279) and only wins the race because server transcoding
 * takes seconds. This is the one sanctioned side-effect reordering in the story
 * (same reorder the image flow documents, sendCommunityImageMessageUseCase.ts:34-42).
 *
 * Flutter bug NOT ported (task-plan line 12; conventions.md § Handling obvious
 * Flutter bugs): `saveCommunityMedia` discards `_uploadCommunityMessageMedia`'s
 * boolean and returns `true` unconditionally (app_storage_service.dart:268-294),
 * so a failed community upload still reports success. The web returns the REAL
 * upload result here — the same documented decision as the image flow
 * (sendCommunityImageMessageUseCase.ts:44-51); the one-line Flutter fix shipped
 * with the image story.
 *
 * Pure-domain contract (conventions.md § Clean Architecture flow): the use case
 * calls only `CommunityService`; it does NOT touch `firestoreService` /
 * `cloudStorageService` / `cloudFunctionsService` directly (the service
 * orchestrates those) and does NOT dispatch to Redux (a presentation concern).
 * Community messages are NOT end-to-end encrypted (no encrypt step — the
 * thumbnail uploads as plaintext).
 */

import { CommunityService } from '@/domain/services/community/communityService';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface SendCommunityVideoMessageArgs {
  /** The video file to upload. */
  file: File;
  /**
   * The video duration in seconds (captured by the composer sheet), fed to the
   * upload's `buildUploadFileInfo`.
   */
  durationSeconds: number;
  /**
   * The ALREADY-BUILT optimistic entity from
   * `CommunityChatMessageRemapper.fromInput` (video branch — Task 16). The hook
   * builds + inserts it into the list before calling this (mirrors the image
   * sibling). Its `messageUsersInfo.owner` selects the storage + Firestore write
   * target, its `id` is the doc id + transcode target, and its
   * `mediaInfo.aspectRatio` is forwarded to the transcode payload.
   */
  message: CommunityChatMessageEntity;
}

export class SendCommunityVideoMessageUseCase {
  private communityService: CommunityService;

  constructor(communityService: CommunityService = CommunityService.getInstance()) {
    this.communityService = communityService;
  }

  /**
   * Upload the video, write the message doc unconditionally, then (on upload
   * success) fire the transcode CF. Returns the REAL upload success boolean.
   *
   * @returns `true`/`false` from `CommunityService.uploadCommunityVideo` (the
   *   message doc is written either way — see step 2 in the class JSDoc), or
   *   `false` on a thrown failure.
   */
  async execute(args: SendCommunityVideoMessageArgs): Promise<boolean> {
    const { file, durationSeconds, message } = args;

    try {
      // (1) Raw video upload first; owner comes from the message
      // (medias_repository.dart:262). Captures the path/extension the transcode
      // payload needs, the tokenized `downloadUrl` for the CF's remote ffprobe,
      // plus the real `uploaded` bool.
      const { uploaded, uploadPath, fileExtension, downloadUrl } =
        await this.communityService.uploadCommunityVideo({
          file,
          communityOwner: message.messageUsersInfo.owner,
          durationSeconds,
        });

      // (2) Write the message doc UNCONDITIONALLY (medias_repository.dart:278-279
      // runs after the upload attempt regardless of the `uploaded` bool).
      await this.communityService.addMessage(message);

      // (3) On upload success only, fire the transcode CF AFTER the doc write
      // (sanctioned reorder — task_12_plan.md:13; image-sibling precedent
      // sendCommunityImageMessageUseCase.ts:34-42 — the CF locates the doc via
      // `where('id','==',messageId)` so it must exist first; the fire is
      // internally un-awaited). Gated on
      // upload success exactly as Flutter's catch-before-fire gates it
      // (app_storage_service.dart:249-265). `aspectRatio` is read off the
      // message's own `mediaInfo` and forwarded for Flutter payload parity
      // (cloud_functions_service.dart:218); the reworked video CF reads/logs it
      // but never writes it, so the persisted mediaInfo.aspectRatio is the
      // client's own optimistic doc write (medias_repository.dart:277), not a CF
      // echo. `dynamicUrl` is threaded from the upload. `durationMillis`
      // is the video duration in MILLISECONDS — Flutter sends the real value for
      // every video (preview_content_screen.dart:192-193 → cloud_functions_service.dart:216);
      // the seconds→ms conversion mirrors Flutter's integer `inMilliseconds`.
      if (uploaded) {
        this.communityService.transcodeCommunityVideo({
          uploadPath,
          fileExtension,
          aspectRatio: message.mediaInfo?.aspectRatio ?? null,
          messageId: message.id,
          dynamicUrl: downloadUrl,
          durationMillis: Math.round(durationSeconds * 1000),
        });
      }

      // (4) Return the REAL upload result (medias_repository.dart:281; the
      // Flutter `return true` bug is NOT ported — see class JSDoc, image story).
      return uploaded;
    } catch (error) {
      appLogger.error(
        'Failed to send community video message',
        'SendCommunityVideoMessageUseCase.execute',
        { error: error as Error },
      );
      return false;
    }
  }
}

// Export singleton instance for convenience (matches the other community use cases).
export const sendCommunityVideoMessageUseCase = new SendCommunityVideoMessageUseCase();
